import {
  type HoverCardDropdownProps,
  HoverCard as MantineHoverCard,
  type HoverCardProps as MantineHoverCardProps,
} from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";

import { PreventEagerPortal } from "metabase/ui";
import { OverlayStackItem } from "metabase/ui/components/overlays/overlay-stack";
export { hoverCardOverrides } from "./HoverCard.config";

export type { HoverCardDropdownProps, HoverCardProps } from "@mantine/core";

const MantineHoverCardDropdown = MantineHoverCard.Dropdown;
const HoverCardDropdown = function Dropdown({
  children,
  ...props
}: HoverCardDropdownProps) {
  return (
    <PreventEagerPortal {...props}>
      <MantineHoverCardDropdown {...props}>
        <OverlayStackItem />
        {children}
      </MantineHoverCardDropdown>
    </PreventEagerPortal>
  );
};
HoverCardDropdown.displayName = MantineHoverCardDropdown.displayName;
MantineHoverCard.Dropdown = HoverCardDropdown;

// Mantine's HoverCard exposes no public way to force-close it (HoverCardProps
// explicitly omits `opened`/`onChange`), and its own Escape handling only
// applies within a HoverCard.Group -- a standalone hover card disables it
// entirely (`useDismiss(context, { enabled: withinGroup })` in Mantine's
// use-hover-card.mjs). Even Popover's own local Escape handler underneath
// can't help: a hover-only-triggered card never moves focus into it, so that
// handler -- which only sees keydowns that pass through the dropdown's own
// DOM subtree -- never receives the event either. (WCAG 1.4.13 Content on
// Hover or Focus -- confirmed via source; this is upstream Mantine behavior,
// not a Metabase misconfiguration.) Remounting the whole subtree via `key` is
// the only lever the public API leaves us to force it closed on Escape.
const HoverCard = function ThemedHoverCard({
  onOpen,
  onClose,
  ...props
}: MantineHoverCardProps) {
  const [resetKey, setResetKey] = useState(0);
  const isOpenRef = useRef(false);

  const handleOpen = useCallback(() => {
    isOpenRef.current = true;
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    isOpenRef.current = false;
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpenRef.current) {
        isOpenRef.current = false;
        setResetKey((key) => key + 1);
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <MantineHoverCard
      key={resetKey}
      onOpen={handleOpen}
      onClose={handleClose}
      {...props}
    />
  );
} as typeof MantineHoverCard;

HoverCard.displayName = MantineHoverCard.displayName;
HoverCard.Target = MantineHoverCard.Target;
HoverCard.Dropdown = MantineHoverCard.Dropdown;
HoverCard.Group = MantineHoverCard.Group;
HoverCard.extend = MantineHoverCard.extend;

export { HoverCard };
